import React from "react";
import { useTranslation } from "react-i18next";

/**
 * The page's editorial workhorse: one heading, one or more body paragraphs, an
 * optional quiet footnote, and an optional image beside the text.
 *
 * Five sections on this page share that shape (no-app, photographer's blind
 * spots, media, privacy, the gallery afterwards), so they share one component
 * rather than five near-identical files. Typography comes from the site's own
 * scale — `heading-50` for the section heading, `body-20` for the prose —
 * matching `ExclusivelyCraftedText`.
 *
 * `note` is for transparency lines (plan-dependent gallery access, original
 * files preserved). It is styled quieter than the body but is ordinary,
 * visible text — never hidden, never a search-engine-only aside.
 *
 * Shared with /digital-wedding-invitation, which is why every string arrives as
 * a key rather than being read from this page's namespace.
 *
 * `image.width` / `image.height` are the file's INTRINSIC size, optional only
 * so the original call sites keep working unchanged. Pass them: the browser
 * reserves the box before the bytes arrive, and the image stops shifting the
 * copy underneath it as it loads.
 *
 * @param {object} props
 * @param {string} props.headingKey       i18n key for the <h2>
 * @param {string[]} props.bodyKeys       i18n keys, one per paragraph
 * @param {string} [props.noteKey]        i18n key for the quiet footnote
 * @param {{src: string, altKey: string, width?: number, height?: number}} [props.image]
 * @param {"left"|"right"} [props.imageSide="right"]
 */
const PhotoSharingEditorial = ({
  headingKey,
  bodyKeys,
  noteKey,
  image,
  imageSide = "right",
}) => {
  const { t } = useTranslation();

  const text = (
    <div className="wps-editorial-text">
      <h2 className="heading-50">{t(headingKey)}</h2>
      {bodyKeys.map((key) => (
        <p key={key} className="body-20">
          {t(key)}
        </p>
      ))}
      {noteKey ? <p className="wps-note">{t(noteKey)}</p> : null}
    </div>
  );

  if (!image) {
    return (
      <section className="section topless wps-section">
        <div className="container">
          <div className="wps-editorial wps-editorial-solo">{text}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="section topless wps-section">
      <div className="container">
        <div
          className={`wps-editorial wps-editorial-split${
            imageSide === "left" ? " is-reversed" : ""
          }`}
        >
          {text}
          <div className="wps-editorial-media">
            <img
              src={image.src}
              alt={t(image.altKey)}
              loading="lazy"
              decoding="async"
              width={image.width}
              height={image.height}
              className="wps-editorial-im"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhotoSharingEditorial;
